import { useEffect, useRef, useState, useCallback } from 'react';
import { io, Socket } from 'socket.io-client';
import { useAuthStore } from '@store/authStore';

const SOCKET_URL = (import.meta.env.VITE_API_URL || 'http://localhost:3000').replace(/\/api\/?$/, '');

export const useOrderSocket = (orderId: string | null) => {
  const { user, tokens } = useAuthStore();
  const socketRef = useRef<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [connectionError, setConnectionError] = useState<string | null>(null);

  useEffect(() => {
    const userId = user?.id;
    const accessToken = tokens?.access_token;

    if (!userId || !accessToken || !orderId) {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
      setIsConnected(false);
      setIsSubscribed(false);
      return;
    }

    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
    }

    const socket = io(`${SOCKET_URL}/events`, {
      auth: {
        token: accessToken,
      },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 5,
    });

    socketRef.current = socket;

    socket.on('connect', () => {
      console.log('✅ Order socket connected:', socket.id);
      setIsConnected(true);
      setConnectionError(null);

      if (orderId) {
        socket.emit('subscribeToOrder', orderId, (response: any) => {
          if (response?.success) {
            setIsSubscribed(true);
            console.log('✅ Subscribed to order:', orderId);
          } else {
            console.error('❌ Failed to subscribe to order:', response);
            setConnectionError('Failed to subscribe to order tracking');
          }
        });
      }
    });

    socket.on('disconnect', (reason) => {
      console.log('❌ Order socket disconnected:', reason);
      setIsConnected(false);
      setIsSubscribed(false);

      // Show connection error if not intentional
      if (reason !== 'io client disconnect') {
        setConnectionError('Connection lost. Reconnecting...');
      }
    });

    socket.on('connect_error', (error) => {
      console.error('Socket connection error:', error);
      setIsConnected(false);
      setConnectionError('Failed to connect. Please check your internet connection.');
    });

    socket.on('reconnect', (attemptNumber) => {
      console.log('🔄 Reconnected after', attemptNumber, 'attempts');
      setConnectionError(null);

      if (orderId) {
        socket.emit('subscribeToOrder', orderId, (response: any) => {
          if (response?.success) {
            setIsSubscribed(true);
          }
        });
      }
    });

    // Cleanup on unmount
    return () => {
      if (socket && orderId) {
        socket.emit('unsubscribeFromOrder', orderId);
      }
      socket.disconnect();
      if (socketRef.current === socket) {
        socketRef.current = null;
      }
    };
  }, [user?.id, tokens?.access_token, orderId]);

  useEffect(() => {
    const accessToken = tokens?.access_token;
    if (!accessToken || !socketRef.current) return;

    socketRef.current.auth = { token: accessToken };

    if (!socketRef.current.connected) {
      socketRef.current.connect();
    }
  }, [tokens?.access_token]);

  /**
   * Subscribe to an event
   */
  const on = useCallback((event: string, handler: (data: any) => void) => {
    if (socketRef.current) {
      socketRef.current.on(event, handler);
    }
  }, []);

  /**
   * Unsubscribe from an event
   */
  const off = useCallback((event: string, handler?: (data: any) => void) => {
    if (socketRef.current) {
      if (handler) {
        socketRef.current.off(event, handler);
      } else {
        socketRef.current.off(event);
      }
    }
  }, []);

  /**
   * Emit an event
   */
  const emit = useCallback((event: string, data?: any) => {
    if (socketRef.current) {
      socketRef.current.emit(event, data);
    }
  }, []);

  return {
    socket: socketRef.current,
    isConnected,
    isSubscribed,
    connectionError,
    on,
    off,
    emit,
  };
};
